'use client'

import { Dialog } from '@vinted/web-ui'
import { useEffect, useState } from 'react'

import { ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  transformLabelOptionsResponse,
  getLabelOptions,
  updateDeliveryOptionChangeRequest,
  isGetLabelOptionsResp,
  LabelOptionsModel,
  isGetLabelOptionsRewriteResp,
  transformLabelOptionsRewriteResponse,
  LabelOptionsRewriteModel,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'

import GenericErrorModal from '../../GenericErrorModal'
import { DeliveryOptionChangeRequestStatus } from '../../../constants'

type Props = {
  isOpen: boolean
  shipmentId?: number
  onClose: () => void
}

const CancelChangeDeliveryOptionRequestModal = ({ isOpen, shipmentId, onClose }: Props) => {
  const translate = useTranslate('conversation.cancel_carrier_change_request_modal')
  const isLabelOptionsRewriteFSEnabled = useFeatureSwitch('web_label_options_rewrite')

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [labelOptionsUiState, setLabelOptionsUiState] = useState(UiState.Idle)
  const [labelOptions, setLabelOptions] = useState<LabelOptionsModel | undefined>()
  const [labelOptionsRewrite, setLabelOptionsRewrite] = useState<
    LabelOptionsRewriteModel | undefined
  >()

  useEffect(() => {
    if (!isOpen || !shipmentId) return

    const fetchLabelOptions = async () => {
      setLabelOptionsUiState(UiState.Pending)
      const response = await getLabelOptions(shipmentId, isLabelOptionsRewriteFSEnabled)

      if ('errors' in response) {
        setLabelOptionsUiState(UiState.Failure)

        return
      }

      if (isGetLabelOptionsRewriteResp(response)) {
        const transformedLabelOptions = transformLabelOptionsRewriteResponse(response)

        setLabelOptionsUiState(UiState.Success)
        setLabelOptionsRewrite(transformedLabelOptions)
      }

      if (isGetLabelOptionsResp(response)) {
        const transformedLabelOptions = transformLabelOptionsResponse(response)

        setLabelOptionsUiState(UiState.Success)
        setLabelOptions(transformedLabelOptions)
      }
    }

    fetchLabelOptions()
  }, [isOpen, shipmentId, isLabelOptionsRewriteFSEnabled])

  const handleSubmitClick = async () => {
    if (!shipmentId) return

    setIsLoading(true)

    const response = await updateDeliveryOptionChangeRequest({
      shipmentId,
      status: DeliveryOptionChangeRequestStatus.Cancelled,
    })

    if (response.code !== ResponseCode.Ok) {
      setIsLoading(false)
      setError(true)
    }

    if (response.code === ResponseCode.Ok) {
      setIsLoading(false)
      reloadPage()
    }
  }

  const handleModalClose = () => {
    setError(false)
    onClose()
  }

  if (isLoading || labelOptionsUiState === UiState.Pending) {
    return (
      <Dialog show>
        <ContentLoader />
      </Dialog>
    )
  }

  if (error) {
    return <GenericErrorModal isShown onClose={handleModalClose} />
  }

  return (
    <Dialog
      title={translate('title')}
      body={translate('modal_body', {
        carrier_name: labelOptionsRewrite?.carrier.name || labelOptions?.carrier.name,
      })}
      testId="cancel-change-delivery-option-request-modal"
      actions={[
        {
          text: translate('actions.confirm'),
          style: Dialog.ActionStyling.Filled,
          callback: handleSubmitClick,
          testId: 'cancel-change-delivery-option-request-modal-confirm-button',
        },
        {
          text: translate('actions.cancel'),
          style: Dialog.ActionStyling.Outlined,
          callback: handleModalClose,
          testId: 'cancel-change-delivery-option-request-modal-close-button',
        },
      ]}
      show={isOpen}
    />
  )
}

export default CancelChangeDeliveryOptionRequestModal
